import React, { useState, useEffect } from 'react';
import { SearchUI } from 'src/components/ui/Search';
import { UserAvatar } from 'src/components/ui/avatar/Avatar';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { searchPeopleSelector } from 'src/components/ui/form/peoplePicker/selectors';
import { searchPeople } from 'src/components/ui/form/peoplePicker/actions';
import { selectUsers } from 'src/modules/users/selector';
import Loading from 'src/components/Loading';
import { UserListItem } from '../../../types/typesVPO';

interface UserSearchProps {
  selectedUsers: UserListItem[];
  onUserFilterChange: (users: UserListItem[]) => void;
  initialSearchValue?: string;
}

type SearchPeopleItem = {
  id?: number | undefined;
  value: string;
  label: string;
  avatar: string | null;
};

interface UserData {
  avatar: string | null;
  shortName: string;
  displayName: string;
  email: string;
}

interface UsersState {
  [email: string]: UserData;
}

export const UserSearch = ({ selectedUsers, onUserFilterChange, initialSearchValue = '' }: UserSearchProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [searchString, setSearchString] = useState(initialSearchValue);

  const dispatch = useAppDispatch();
  const users: UsersState = useAppSelector(selectUsers);
  const people = useAppSelector(searchPeopleSelector);
  const status = useAppSelector((state) => state.searchUsers.status);

  useEffect(() => {
    if (searchString.length >= 3) {
      dispatch(searchPeople(searchString));
    }
  }, [searchString, dispatch]);

  const handleUserSelect = (person: SearchPeopleItem) => {
    const newUser: UserListItem = {
      id: person.id,
      email: person.value,
      title: person.label,
      avatar: person.avatar,
      shortName: person.label
        .split(' ')
        .map((n) => n[0])
        .join(''),
    };

    const isSelected = selectedUsers.some((u) => u.email === person.value);

    if (isSelected) {
      onUserFilterChange(selectedUsers.filter((u) => u.email !== person.value));
    } else {
      onUserFilterChange([...selectedUsers, newUser]);
    }

    setIsExpanded(false);
  };

  const handleUncheck = (userToRemove: UserListItem) => {
    const updatedUsers = selectedUsers.filter((user) => user.email !== userToRemove.email);
    onUserFilterChange(updatedUsers);
  };

  return (
    <div className='px-5 pb-5'>
      <div className='relative mb-4'>
        <SearchUI
          placeholder='Найти ответственного'
          defaultValue={searchString}
          inputClass="font-medium"
          onClick={(value) => {
            setSearchString(value);
            setIsExpanded(true);
          }}
        />
      </div>
      {/* Список пользователей после поиска */}
      {isExpanded && (
        <div className='mt-2 max-h-80 overflow-y-auto scrollbar scrollbar-thumb-rounded-lg scrollbar-h-2 scrollbar-w-2 scrollbar-thumb-[#D1D5DB]'>
          {status === 'LOADING' ? (
            <div className='flex items-center justify-center p-4'>
              <Loading />
            </div>
          ) : (
            people.map((person) => {
              const isSelected = selectedUsers.some((u) => u.email === person.value);

              return (
                <label key={person.value} className='flex items-center gap-3 py-2 hover:bg-gray-50 cursor-pointer text-black w-full' onClick={() => handleUserSelect(person)}>
                  <div className='relative flex items-center'>
                    <input type='checkbox' checked={isSelected} onChange={() => handleUserSelect(person)} onClick={(e) => e.stopPropagation()} className='sr-only' />
                    <div className='flex items-center justify-center w-5 h-5 border rounded border-darkblue'>
                      {isSelected && (
                        <span className='opacity-100'>
                          <svg width='11' height='8' viewBox='0 0 11 8' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <path
                              d='M10.0915 0.951972L10.0867 0.946075L10.0813 0.940568C9.90076 0.753564 9.61034 0.753146 9.42927 0.939309L4.16201 6.22962L1.58507 3.63469C1.40401 3.44841 1.11351 3.44879 0.932892 3.63584C0.755703 3.81933 0.755703 4.10875 0.932892 4.29224L0.932878 4.29225L0.934851 4.29424L3.58046 6.95832C3.73676 7.11955 3.94983 7.2 4.1473 7.2C4.36196 7.2 4.55963 7.11773 4.71406 6.9584L10.0468 1.60234C10.2436 1.4199 10.2421 1.1339 10.0915 0.951972ZM4.2327 6.30081L4.2317 6.2998C4.23206 6.30015 4.23237 6.30049 4.23269 6.30082L4.2327 6.30081Z'
                              fill='#012169'
                              stroke='#012169'
                              strokeWidth='0.4'
                            />
                          </svg>
                        </span>
                      )}
                    </div>
                  </div>
                  <UserAvatar avatar={users[person.value]?.avatar} shortName={users[person.value]?.shortName} displayName={users[person.value]?.displayName} email={person.value} />
                  <div className='flex flex-col'>
                    <span className='text-sm text-gray-900'>{person.label}</span>
                  </div>
                </label>
              );
            })
          )}
          {status === 'SUCCESS' && searchString.length >= 3 && people.length === 0 && (
            <div className='p-4 text-center text-gray-500'>
              Нет совпадений по запросу <strong>{searchString}</strong>
            </div>
          )}
        </div>
      )}
      {/* Список выбранных */}
      {selectedUsers.length > 0 && (
        <div className='border-t mt-2'>
          <div className='px-3 pb-3'>
            {selectedUsers.map((user) => (
              <label key={user.email} className='flex items-center gap-3 py-2 hover:bg-gray-50 cursor-pointer text-black w-full'>
                <div className='relative flex items-center'>
                  <input type='checkbox' checked={true} onChange={() => handleUncheck(user)} className='sr-only' />
                  <div className='flex items-center justify-center w-5 h-5 border rounded border-darkblue'>
                    <span className='opacity-100'>
                      <svg width='11' height='8' viewBox='0 0 11 8' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path
                          d='M10.0915 0.951972L10.0867 0.946075L10.0813 0.940568C9.90076 0.753564 9.61034 0.753146 9.42927 0.939309L4.16201 6.22962L1.58507 3.63469C1.40401 3.44841 1.11351 3.44879 0.932892 3.63584C0.755703 3.81933 0.755703 4.10875 0.932892 4.29224L0.932878 4.29225L0.934851 4.29424L3.58046 6.95832C3.73676 7.11955 3.94983 7.2 4.1473 7.2C4.36196 7.2 4.55963 7.11773 4.71406 6.9584L10.0468 1.60234C10.2436 1.4199 10.2421 1.1339 10.0915 0.951972ZM4.2327 6.30081L4.2317 6.2998C4.23206 6.30015 4.23237 6.30049 4.23269 6.30082L4.2327 6.30081Z'
                          fill='#012169'
                          stroke='#012169'
                          strokeWidth='0.4'
                        />
                      </svg>
                    </span>
                  </div>
                </div>
                <UserAvatar avatar={users[user.email]?.avatar} shortName={users[user.email]?.shortName} displayName={users[user.email]?.displayName} email={user.email} />
                <div className='flex flex-col'>
                  <span className='text-sm text-gray-900'>{user.title}</span>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
